import { storageAdapter, OPTIN_STATUS, loadConfiguration, STORAGE_KEY, SCRIPT_NAME } from './public';
import { cookieStorageAdapter, localStorageAdapter } from './storage';
let idIndex = 0;
/**
 * Create a new unique ID to reference elements
 */
const elementId = () => `toie${++idIndex}`;
const VERSION_STORAGE_KEY = '__trackingOptInVersion';
/**
 * Creates a new {@link Element}
 * @param tagName Name of an element to create through the DOM or a function
 *      creating an Element.
 * @param props Options for the element. If the element is an HTML element, the
 *      props will be set with their getter, if possible. If not, they will be
 *      set as attribute with the specified value.
 *
 *      Props prefixed with `on` will be applied as event handlers, e.g.
 *      `onClick` will be attached as `click` event listener.
 *
 *      If the `tagName` is a function, the `props` are passed as argument.
 * @param children Children to append to the element. If the `tagName` is a
 *      function, the `children` will be passed as a member `children` in the
 *      first argument to the function.
 * @returns The {@link Element} created
 */
const createElement = (tagName, props = {}, children = []) => {
    let el;
    if (typeof tagName === 'function') {
        el = tagName(Object.assign(Object.assign({}, props), { children }));
    }
    else {
        el = document.createElement(tagName);
        Object.keys(props).forEach(k => {
            if (props[k] === null) {
                return;
            }
            if (k.substr(0, 2) === 'on') {
                el.addEventListener(k.substr(2).toLowerCase(), props[k]);
                return;
            }
            el[k] !== undefined ? el[k] = props[k] : el.setAttribute(k, '' + props[k]);
        });
        children.forEach(c => c && el.appendChild(typeof c === 'object' ? c : document.createTextNode('' + c)));
    }
    return el;
};
/**
 * Filter an object by its entries.
 * @param obj The dictionary to filter
 * @param predicate The predicate to evalute the dictionary entries
 * @returns A new object with only the filtered entries
 */
const filterDict = (obj, predicate) => Object.keys(obj).reduce((c, k) => predicate(obj[k]) ? (c[k] = obj[k], c) : c, {});
const consentOptIn = (config) => {
    if (config.storage && config.storage.type === 'cookie') {
        storageAdapter.instance = cookieStorageAdapter(config.storage.options || {});
    }
    else {
        storageAdapter.instance = localStorageAdapter();
    }
    const listeners = {};
    let popupWindow = null;
    const registerListener = (service, listener) => {
        if (!(service in listeners)) {
            listeners[service] = [];
        }
        listeners[service].push(listener);
    };
    const enableScriptTags = (serviceId) => (status) => {
        if (status === OPTIN_STATUS.GRANTED) {
            const scriptTags = document.querySelectorAll(`script[data-service="${serviceId}"]`);
            for (let i = 0; i < scriptTags.length; i++) {
                const tag = scriptTags[i];
                const newTag = document.createElement('script');
                for (let a = 0; a < tag.attributes.length; a++) {
                    const attr = tag.attributes[a];
                    if (attr.name === 'type') {
                        continue;
                    }
                    newTag.setAttribute(attr.name, attr.value);
                }
                newTag.innerHTML = tag.innerHTML;
                tag.parentNode.insertBefore(newTag, tag);
                tag.parentNode.removeChild(tag);
            }
        }
    };
    const enableIframes = (serviceId) => (status) => {
        if (status === OPTIN_STATUS.GRANTED) {
            const iframes = document.querySelectorAll(`iframe[data-service=${serviceId}]`);
            for (let i = 0; i < iframes.length; i++) {
                const iframe = iframes[i];
                if (!iframe.hasAttribute('data-src')) {
                    return;
                }
                iframe.src = iframe.getAttribute('data-src');
                iframe.setAttribute('data-invoked', 'true');
                iframe.removeAttribute('data-src');
            }
        }
    };
    let state = loadConfiguration();
    const getServiceStatus = (serviceId) => state[serviceId] || OPTIN_STATUS.UNSPECIFIED;
    const setServiceStatus = (serviceId, status) => {
        state[serviceId] = typeof status === 'boolean'
            ? status
                ? OPTIN_STATUS.GRANTED
                : OPTIN_STATUS.DENIED
            : status;
    };
    const getServiceStatusLabel = (active) => config.popupLabels[active ? 'enabled' : 'disabled'];
    /**
     * @param description Description text
     * @param collapsed Whether the description is collapsed by default
     * @param label Label of the description toggle
     * @returns A tuple with the button and the paragraph containing the
     *      description with respective attributes and events for collapsing the
     *      text.
     *
     *      If no description is given, it will return `null` for both.
     */
    const Info = (description, collapsed = false, label = 'info') => {
        if (!description) {
            return [null, null];
        }
        const id = elementId();
        const button = createElement('button', {
            className: 'tracking-optin__info-button',
            type: 'button',
            'aria-expanded': collapsed ? 'false' : 'true',
            'aria-controls': id,
            'aria-label': label,
            onClick: (e) => e.target.setAttribute('aria-expanded', e.target.getAttribute('aria-expanded') === 'false' ? 'true' : 'false'),
        }, ['i']);
        const text = createElement('p', { className: 'tracking-optin__info-text copy', id, innerHTML: description });
        return [button, text];
    };
    /**
     * Creates a switch component with relevant properties
     * @param props
     */
    const SwitchButton = (props) => {
        let active = props.active;
        const switchId = elementId();
        const statusLabelId = elementId();
        const updateStatusLabel = () => {
            const el = document.getElementById(statusLabelId);
            el.innerText = getServiceStatusLabel(active);
        };
        const onChange = (e) => {
            active = props.onClick(e);
            updateStatusLabel();
        };
        return createElement('div', { className: 'tracking-optin__switch__container' }, [
            createElement('button', {
                role: 'switch',
                id: switchId,
                type: 'button',
                'aria-checked': active ? 'true' : 'false',
                'aria-labelledby': props.labelledBy,
                'aria-describedby': props.describedBy,
                className: 'tracking-optin__switch',
                disabled: props.disabled,
                onClick: onChange,
            }),
            createElement('label', {
                id: statusLabelId,
                className: 'tracking-optin__switch__status',
                htmlFor: switchId,
            }, [getServiceStatusLabel(active)]),
        ]);
    };
    /**
     * Creates a service with description and necessary controls based on the
     * {@link TrackingOptInOptions} for a specific ID.
     * @param props
     */
    const Service = (props) => {
        const [infoButton, infoText] = Info(props.service.description, props.service.descriptionCollapsed !== false, `info: ${props.service.label}`);
        const infoTextId = infoText ? infoText.id : null;
        const titleId = elementId();
        const serviceEnabled = ({
            [OPTIN_STATUS.UNSPECIFIED]: props.group.required || props.service.defaultValue === true,
            [OPTIN_STATUS.GRANTED]: true,
            [OPTIN_STATUS.DENIED]: false,
        })[getServiceStatus(props.serviceId)];
        const handleServiceChange = (e) => {
            const v = e.currentTarget.getAttribute('aria-checked') === 'false';
            e.currentTarget.setAttribute('aria-checked', '' + v);
            setServiceStatus(props.serviceId, v);
            return v;
        };
        return createElement('li', { className: 'tracking-optin__service' }, [
            createElement('h3', { id: titleId }, [props.service.label]),
            infoButton,
            infoText,
            createElement(SwitchButton, {
                active: serviceEnabled,
                labelledBy: titleId,
                describedBy: infoTextId,
                disabled: props.group.required || null,
                onClick: handleServiceChange,
            }),
        ]);
    };
    /**
     * Creates a service-group for a specific group ID based on the given
     * {@link TrackingOptInOptions}.
     * @param props
     */
    const Group = (props) => {
        const services = filterDict(config.services, s => s.group === props.groupId);
        const [infoButton, infoText] = Info(props.group.description);
        return createElement('li', { className: 'tracking-optin__group' }, [
            createElement('h2', {}, [props.group.label]),
            infoButton,
            infoText,
            createElement('ul', {}, Object.keys(services).map(serviceId => Service({
                serviceId,
                service: config.services[serviceId],
                group: props.group,
            }))),
        ]);
    };
    /**
     * Internal helper used when a service is updated
     * @param serviceId ID of the service that changed
     * @param status The new status of the service
     */
    const handleServiceChange = (serviceId, status) => {
        if (listeners[serviceId]) {
            listeners[serviceId].forEach(cb => cb(status));
        }
    };
    /**
     * Unmounts the popup-window if present
     */
    const destroyWindow = () => {
        if (popupWindow) {
            popupWindow.parentNode.removeChild(popupWindow);
            popupWindow = null;
        }
    };
    /**
     * "closes" the popup window
     */
    const closeWindow = () => {
        if (popupWindow) {
            popupWindow.classList.remove('visible');
        }
    };
    /**
     * Updates the configuration and triggers the necessary events
     * @param e
     */
    const handleSave = (e) => {
        if (e && e.preventDefault) {
            e.preventDefault();
        }
        const currentState = loadConfiguration();
        Object.keys(config.services).forEach(serviceId => {
            const service = config.services[serviceId];
            const group = config.groups[service.group];
            const value = group && group.required ? true : (state[serviceId] !== undefined ? state[serviceId] : (service.defaultValue || false));
            state[serviceId] = typeof value === 'boolean' ? (value ? OPTIN_STATUS.GRANTED : OPTIN_STATUS.DENIED) : value;
            if (currentState[serviceId] !== state[serviceId]) {
                handleServiceChange(serviceId, state[serviceId]);
            }
        });
        if (config.version) {
            storageAdapter.instance.set(VERSION_STORAGE_KEY, config.version);
        }
        storageAdapter.instance.set(STORAGE_KEY, JSON.stringify(state));
        closeWindow();
    };
    /**
     * Creates and mounts the popup window based on the
     * {@link TrackingOptInOptions}.
     */
    const buildWindow = () => {
        if (popupWindow) {
            return popupWindow;
        }
        const frag = document.createDocumentFragment();
        const allServices = Object.keys(config.services).map(k => config.services[k]);
        const popup = createElement('aside', { className: 'tracking-optin__popup' }, [
            createElement('form', {
                onSubmit: handleSave,
            }, [
                createElement('h1', {}, [config.popupLabels.title]),
                createElement('p', { className: 'copy', innerHTML: config.popupLabels.description }),
                createElement('ul', {}, Object.keys(config.groups)
                    .filter(group => allServices.some(s => s.group === group))
                    .map(group => Group({ groupId: group, group: config.groups[group] }))),
                createElement('button', { className: 'button button--primary', onClick: handleSave }, [config.popupLabels.save]),
            ]),
        ]);
        frag.appendChild(popup);
        document.body.insertBefore(frag, document.body.firstChild);
        popupWindow = document.querySelector('.tracking-optin__popup');
        return popupWindow;
    };
    /**
     * Resets the stored consent options
     */
    const reset = () => {
        destroyWindow();
        Object.keys(loadConfiguration()).forEach(serviceId => {
            handleServiceChange(serviceId, OPTIN_STATUS.UNSPECIFIED);
        });
        storageAdapter.instance.remove(STORAGE_KEY);
        state = {};
    };
    /**
     * Displays the consent banner. If necessary, the banner is built and
     * mounted.
     */
    const showOptions = () => {
        if (config.disablePopup) {
            return;
        }
        const popup = buildWindow();
        popup.classList.add('visible');
    };
    const clearHash = () => {
        history.replaceState('', document.title, window.location.pathname + window.location.search);
    };
    /**
     * Setup the element
     */
    const init = () => {
        Object.keys(config.services).forEach(serviceId => {
            registerListener(serviceId, enableScriptTags(serviceId));
            registerListener(serviceId, enableIframes(serviceId));
            handleServiceChange(serviceId, state[serviceId] || OPTIN_STATUS.UNSPECIFIED);
        });
        const handleHash = () => {
            if (document.location.hash === '#consentOptions') {
                showOptions();
                clearHash();
            }
            else if (document.location.hash === '#resetConsentOptions') {
                reset();
                showOptions();
                clearHash();
            }
        };
        window.addEventListener('hashchange', handleHash);
        handleHash();
    };
    return {
        on: registerListener,
        getServiceStatus,
        setServiceStatus: (service, status) => {
            setServiceStatus(service, status);
            handleSave();
        },
        showOptions,
        reset,
        init,
    };
};
const INITIALIZED = '__isinit';
(() => {
    if (window[SCRIPT_NAME] && !window[SCRIPT_NAME][INITIALIZED]) {
        const config = window[SCRIPT_NAME].c;
        if (!config || config.disabled === false) {
            return;
        }
        const listeners = window[SCRIPT_NAME].l;
        const optIn = consentOptIn(config);
        window[SCRIPT_NAME] = optIn;
        window[SCRIPT_NAME][INITIALIZED] = true;
        listeners.map(([s, c]) => optIn.on(s, c));
        optIn.init();
        if (storageAdapter.instance.get(STORAGE_KEY) === null
            || (!!config.version
                && storageAdapter.instance.get(VERSION_STORAGE_KEY) !== config.version)) {
            optIn.showOptions();
        }
    }
})();
